import { Link, useNavigate } from "@tanstack/react-router"
import { useEffect } from "react"
import {
  Button,
  Card,
  CardAction,
  CardContent,
  CardTitle,
  Juggling,
  JugglingScreen,
} from "@motleyhand/design"
import { useCreatePromptMutation } from "@/prompting"
import { useGetProviderKeysQuery } from "@/secrets"
import { useGetLatestRevisionQuery } from "@/common"
import { useCurrentTenant } from "@/tenant"

const routeByType = {
  prompt: "/t/$tenantSlug/prompts/$id",
  workflow: "/t/$tenantSlug/workflows/$id",
  evaluation: "/t/$tenantSlug/evaluations/$id",
} as const

export default function DashboardPage() {
  const { tenantSlug } = useCurrentTenant()
  const navigate = useNavigate()
  const { data: latestRevision, isLoading } = useGetLatestRevisionQuery()
  const [createPrompt, { isLoading: isCreating }] = useCreatePromptMutation()
  const { data: providerKeys, isSuccess: keysLoaded } =
    useGetProviderKeysQuery()

  useEffect(() => {
    if (isLoading || !latestRevision) {
      return
    }

    void navigate({
      to: routeByType[latestRevision.type],
      params: { tenantSlug, id: latestRevision.definitionId },
      replace: true,
    })
  }, [isLoading, latestRevision, navigate, tenantSlug])

  if (isLoading || latestRevision) {
    return <JugglingScreen />
  }

  const handleCreatePrompt = async () => {
    const { id } = await createPrompt().unwrap()
    await navigate({
      to: "/t/$tenantSlug/prompts/$id",
      params: { tenantSlug, id },
    })
  }

  return (
    <div className="flex h-full w-full items-center justify-center">
      <Card className="flex flex-col items-center justify-center p-4">
        <CardTitle className="font-title text-xl">
          Create your first prompt
        </CardTitle>
        <CardContent className="mb-2 text-muted-foreground">
          Get started by creating a prompt template.
        </CardContent>
        <CardAction className="w-full text-center">
          <Button disabled={isCreating} onClick={handleCreatePrompt}>
            {isCreating && <Juggling />}
            Create Prompt
          </Button>
        </CardAction>
        {keysLoaded && !providerKeys.length && (
          <p className="text-xs text-muted-foreground">
            You&apos;ll need your API key to run prompts. →{" "}
            <Link
              to="/t/$tenantSlug/settings"
              params={{ tenantSlug }}
              className="underline"
            >
              Set up in Settings
            </Link>
          </p>
        )}
      </Card>
    </div>
  )
}
